"use client";

import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

export default function ExportFolderDropdownMenuItem({
    folderId,
}: {
    folderId: string;
}) {
    const { locale } = useLocale();

    const exportFolder = async () => {
        const res = await client.api.storage.folder[":folderId"].export.$get({
            param: {
                folderId: folderId,
            },
        });

        if (!res.ok) {
            throw new Error("Failed to export folder");
        }

        const { data } = await res.json();

        // Map headers from English to Hebrew
        const headers = {
            id: t("storage.export.csv.header.id"),
            name: t("storage.export.csv.header.name"),
            embedUrl: t("storage.export.csv.header.embed-url"),
            folderPath: t("storage.export.csv.header.folder-path"),
        };

        const toCsvValue = (value: string) => `"${value.replace(/"/g, '""')}"`;

        const collator = new Intl.Collator(locale, { numeric: true });
        const rows = [...data].sort(
            (a, b) =>
                collator.compare(a.folderPath, b.folderPath) ||
                collator.compare(a.name ?? "", b.name ?? "")
        );

        // Convert data to CSV format
        const csvContent = [
            Object.values(headers).map(toCsvValue).join(","),
            ...rows.map((item) =>
                [item.id, item.name ?? "", item.embedUrl, item.folderPath]
                    .map(toCsvValue)
                    .join(",")
            ),
        ].join("\n");

        // BOM keeps Hebrew names readable when Excel opens the file
        const blob = new Blob(["\uFEFF" + csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", t("storage.export.csv.filename"));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <DropdownMenuItem
            onClick={(e) => {
                e.stopPropagation();
                toast.promise(
                    exportFolder(),
                    {
                        loading: t("toast.loading.export-folder"),
                        success: t("toast.success.export-folder"),
                        error: t("toast.error.export-folder"),
                    },
                    {
                        loading: {
                            keyName: "toast.loading.export-folder",
                        },
                        success: {
                            keyName: "toast.success.export-folder",
                        },
                        error: {
                            keyName: "toast.error.export-folder",
                        },
                    }
                );
            }}
        >
            {t("storage.folders-grid.item.options.export", undefined, undefined)}
        </DropdownMenuItem>
    );
}
